/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import * as React from "react";
import { useEffect, useState } from "react";

// react-router-dom components
import { useNavigate, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// sweetalert2 components
import Swal from "sweetalert2";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Icon from "@mui/material/Icon";
import { styled } from "@mui/system";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";

// Wizard page components
import Provider from "layouts/applications/wizard/components/Provider";
import AvailableTemplates from "layouts/applications/wizard/components/AvailableTemplates";
import SportsEventType from "layouts/applications/wizard/components/SportsEventType";
import Sports from "layouts/applications/wizard/components/Sports";
import Competitions from "layouts/applications/wizard/components/Competitions";
import SaveTemplate from "layouts/applications/wizard/components/SaveTemplate";
import { Button } from "@mui/material";
import CheckFilterCreation from "Modals/CheckFilterCreation";

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";
import { useSoftUIController } from "context";

// Images
import logoBet365 from "assets/images/providers/bet365.png";
import logoVbet from "assets/images/providers/vbet.png";
import logo1xbet from "assets/images/providers/onexbet.png";
import logoStoiximan from "assets/images/providers/stoiximan.png";
import logoMozzartBet from "assets/images/providers/mozzartbet.png";
import logoBetradar from "assets/images/providers/betradar.png";
import logoWilliamhill from "assets/images/providers/williamhill.png";
import logoBetanode from "assets/images/providers/betano-logo.png";
import logoDraftkings from "assets/images/providers/draftkings.png";
import logoBwin from "assets/images/providers/bwin.png";
import logoPaddypower from "assets/images/providers/paddypower.png";
import logoSbobet from "assets/images/providers/sbobet.png";
import logo188bet from "assets/images/providers/188bet.png";
import logoSportingbet from "assets/images/providers/sportingbet.png";
import logoUnibet from "assets/images/providers/unibet.png";
import logoFeedTheBet from "assets/images/providers/feedthebet.png";
import logoFuse from "assets/images/providers/fuse.png";

import DialogBox from "Modals/CheckLeavePage";
import { useCallbackPrompt } from "hooks/useCallbackPrompt";

const globalCss = require("assets/theme/base/globals");

function getSteps() {
  return [
    "Choose Provider",
    "Choose Sports",
    "Choose Event Type",
    "Choose Competitions",
    "Save Filter",
  ];
}

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

function Wizard({ template, mode, filterProvider, filterSports, filterCompetitions, filter }) {
  let href = window.location.href;
  // if (!href) return;
  // //console.log("href: ", href);
  // filter = filter ? filter : href.split("edit-filter/")[1];
  // template = template
  //   ? template
  //   : href.indexOf("templates") > -1
  //   ? href.split("templates/")[1].split("/")[0]
  //   : "0";

  const [controller] = useSoftUIController();
  const { platform, permissions } = controller;
  const location = useLocation();

  const [locationStateFilter, setLocationStateFilter] = useState(null);
  const [locationStateTemplate, setLocationStateTemplate] = useState(null);

  const [previousStep, setPreviousStep] = useState(filterProvider ? 0 : -1);
  const [activeStep, setActiveStep] = useState(filterProvider ? 1 : 0);
  const [stepDisabled, setStepDisabled] = useState(filterProvider ? false : true);
  const [showCreateModal, setCreateModal] = useState(false);

  const [initProviders, setInitProviders] = useState([]);
  const [provider, setProvider] = useState(filterProvider || null);
  const [initSports, setInitSports] = useState([]);
  const [sports, setSports] = useState(filterSports || null);
  const [initCompetitions, setInitCompetitions] = useState([]);
  const [competitions, setCompetitions] = useState(filterCompetitions || null);

  const [sportsArray, setSportsArray] = useState([]);
  const [competitionsArray, setCompetitionsArray] = useState([]);
  const [oddsFilters, setOddsFilters] = useState({});

  const [competitionInit, setCompetitionInit] = useState(true);
  const [templateId, setTemplateId] = useState(template);
  const [templateData, setTemplateData] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [showSportPromosSwitch, setShowSportPromosSwitch] = useState(false);
  const [showCompetitionPromosSwitch, setShowCompetitionPromosSwitch] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showDialog);

  const mixingOptions = [
    { value: 0, label: "No Mixing" },
    { value: 1, label: "Highest Price" },
    { value: 2, label: "Percentage Weight" },
  ];

  const oddsReactions = [
    { value: 0, label: "Do Nothing" },
    { value: 1, label: "Suspend Odds" },
    { value: 2, label: "Set Odds to Limits" },
    { value: 3, label: "Suspend Market" },
  ];

  const marginOptions = [
    { value: 0, label: "No Margin" },
    { value: 1, label: "All odds get same margin (EM)" },
    { value: 2, label: "Highest odds get more margin (MPTO)" },
    { value: 3, label: "Lowest odds get more margin (MPTP)" },
  ];

  const oddsSuspensionType = [
    { value: 0, label: "Never" },
    { value: 1, label: "One Provider Suspend Odd" },
    { value: 2, label: "All Providers Suspend Odd" },
    { value: 3, label: "The Most off the Providers Suspend Odd" },
    { value: 4, label: "Ordered Providers Suspend Odd" },
    { value: 5, label: "Price is higher than the sorted Providers" },
  ];

  const steps = getSteps();
  const isLastStep = activeStep === steps.length - 1;

  const css = globalCss ? globalCss.default : null;
  const customColors = css.customColors;

  const hasPermission = (permissionsToCheck, permissionId) => {
    if (!permissionsToCheck || permissionsToCheck.length === 0) return false;

    const id = Number(permissionId);

    return permissionsToCheck.some((permission) => {
      if (permission === "super") return true;

      if (typeof permission === "number") return permission === id;
      if (typeof permission === "string") return permission === String(permissionId);

      if (!permission || typeof permission !== "object") return false;

      if (permission.id != null && Number(permission.id) === id) return true;
      if (permission.permission_id != null && Number(permission.permission_id) === id) return true;
      if (permission.value != null && Number(permission.value) === id) return true;

      return false;
    });
  };

  const isAdminUser = (userData) => {
    if (!userData) return false;

    if (Number(userData.role_id) === 1) return true;

    return Array.isArray(userData.roles)
      ? userData.roles.some((role) => Number(role.id) === 1)
      : false;
  };

  async function fetchPlatformPromosStatus() {
    const selectedPlatformId = platform && platform.value ? platform.value : null;
    if (!selectedPlatformId) return false;

    const getPlatformEndpoint = `/api/v1/platforms/${selectedPlatformId}`;
    const response = await getData(getPlatformEndpoint, { Authorization: getAuthorizationHeader() });
    if (!response || response.data == null) return false;

    const platformConfig = Array.isArray(response.data) ? response.data[0] : response.data;
    if (!platformConfig) return false;

    return !!platformConfig.promos;
  }

  async function fetchPromoPermissions() {
    let canUseSportPromos = hasPermission(permissions, 37);
    let canUseCompetitionPromos = hasPermission(permissions, 38);
    const userData = getUserData();

    if (isAdminUser(userData)) {
      canUseSportPromos = true;
      canUseCompetitionPromos = true;
    }

    if (!canUseSportPromos || !canUseCompetitionPromos) {
      if (userData && userData.id) {
        const getPermissionsEndpoint = `/api/v1/users/${userData.id}/permissions`;
        const response = await getData(getPermissionsEndpoint, {
          Authorization: getAuthorizationHeader(),
        });

        if (response && Array.isArray(response.data)) {
          canUseSportPromos = canUseSportPromos || hasPermission(response.data, 37);
          canUseCompetitionPromos = canUseCompetitionPromos || hasPermission(response.data, 38);
        }
      }
    }

    const platformPromosEnabled = await fetchPlatformPromosStatus();

    setShowSportPromosSwitch(canUseSportPromos && platformPromosEnabled);
    setShowCompetitionPromosSwitch(canUseCompetitionPromos && platformPromosEnabled);
  }

  function extractOddSettings(provider) {
    let oddSettings = { inplay_odds_settings: {}, pregame_odds_settings: {} };

    // barrier_reaction_type_inplay: 3 v
    // barrier_reaction_type_pregame: 3 v
    // inplay_odds_filter_id: 209
    // margin_percent_inplay: 1.1
    // margin_percent_pregame: 1.1
    // margin_type_inplay: 3
    // margin_type_pregame: 3
    // max_odd_barrier_inplay: 1000
    // max_odd_barrier_pregame: 1000
    // min_odd_barrier_inplay: 1
    // min_odd_barrier_pregame: 1
    // mixing_odd_limit_inplay: 10
    // mixing_odd_limit_pregame: 10
    // mixing_odd_type_inplay: 2
    // mixing_odd_type_pregame: 2
    oddSettings.inplay_odds_settings.static_odd_reaction = provider.static_odd_reaction_json_inplay;
    oddSettings.pregame_odds_settings.static_odd_reaction =
      provider.static_odd_reaction_json_pregame;

    oddSettings.inplay_odds_settings.odds_filter_inplay_id = provider.inplay_odds_filter_id;
    oddSettings.pregame_odds_settings.odds_filter_pregame_id = provider.pregame_odds_filter_id;

    oddSettings.inplay_odds_settings.oddsLimitReaction_inplay = oddsReactions.find(
      (or) => or.value == provider.barrier_reaction_type_inplay
    );
    oddSettings.inplay_odds_settings.oddsLimitReaction_inplay = oddSettings.inplay_odds_settings
      .oddsLimitReaction_inplay
      ? oddSettings.inplay_odds_settings.oddsLimitReaction_inplay.label
      : "";
    oddSettings.inplay_odds_settings.lowerlimit_inplay = provider.min_odd_barrier_inplay;
    oddSettings.inplay_odds_settings.upperlimit_inplay = provider.max_odd_barrier_inplay;
    oddSettings.inplay_odds_settings.mixingType_inplay = mixingOptions.find(
      (mo) => mo.value == provider.mixing_odd_type_inplay
    );
    oddSettings.inplay_odds_settings.mixingType_inplay = oddSettings.inplay_odds_settings
      .mixingType_inplay
      ? oddSettings.inplay_odds_settings.mixingType_inplay.label
      : "";
    oddSettings.inplay_odds_settings.mixing_providers_inplay =
      typeof provider.mixing_providers_inplay == "string"
        ? provider.mixing_providers_inplay.replace(/[{,},"]/g, "")
        : provider.mixing_providers_inplay;
    if (provider.mixing_providers_inplay == "NULL" || !provider.mixing_providers_inplay) {
      oddSettings.inplay_odds_settings.mixing_providers_inplay = [];
    }
    oddSettings.inplay_odds_settings.mixingProviders_inplay =
      typeof provider.mixing_providers_inplay == "object"
        ? provider.mixing_providers_inplay.join(",")
        : "";
    oddSettings.inplay_odds_settings.factor_inplay = provider.static_odd_reaction_inplay
      ? provider.static_odd_reaction_inplay.toString()
      : "0";
    oddSettings.inplay_odds_settings.randomFactor_inplay = provider.random_odd_reaction_inplay
      ? "Random Apply"
      : "Allways Apply";
    oddSettings.inplay_odds_settings.marginType_inplay = marginOptions.find(
      (mo) => mo.value == provider.margin_type_inplay
    );
    oddSettings.inplay_odds_settings.marginType_inplay = oddSettings.inplay_odds_settings
      .marginType_inplay
      ? oddSettings.inplay_odds_settings.marginType_inplay.label
      : "";
    oddSettings.inplay_odds_settings.marginPercent_inplay = provider.margin_percent_inplay;
    oddSettings.inplay_odds_settings.suspension_type_inplay = oddsSuspensionType.find(
      (ost) => ost.value == provider.suspension_type_inplay
    );

    //-----

    oddSettings.pregame_odds_settings.oddsLimitReaction_pregame = oddsReactions.find(
      (or) => or.value == provider.barrier_reaction_type_pregame
    );
    oddSettings.pregame_odds_settings.oddsLimitReaction_pregame = oddSettings.pregame_odds_settings
      .oddsLimitReaction_pregame
      ? oddSettings.pregame_odds_settings.oddsLimitReaction_pregame.label
      : "";
    oddSettings.pregame_odds_settings.lowerlimit_pregame = provider.min_odd_barrier_pregame;
    oddSettings.pregame_odds_settings.upperlimit_pregame = provider.max_odd_barrier_pregame;
    oddSettings.pregame_odds_settings.mixingType_pregame = mixingOptions.find(
      (mo) => mo.value == provider.mixing_odd_type_pregame
    );
    oddSettings.pregame_odds_settings.mixingType_pregame = oddSettings.pregame_odds_settings
      .mixingType_pregame
      ? oddSettings.pregame_odds_settings.mixingType_pregame.label
      : "";
    oddSettings.pregame_odds_settings.mixing_providers_pregame =
      typeof provider.mixing_providers_pregame == "string"
        ? provider.mixing_providers_pregame.replace(/[{,},"]/g, "")
        : provider.mixing_providers_pregame;
    if (provider.mixing_providers_pregame == "NULL" || !provider.mixing_providers_pregame) {
      oddSettings.pregame_odds_settings.mixing_providers_pregame = [];
    }
    oddSettings.pregame_odds_settings.mixingProviders_pregame =
      typeof provider.mixing_providers_pregame == "object"
        ? provider.mixing_providers_pregame.join(",")
        : "";
    oddSettings.pregame_odds_settings.factor_pregame = provider.static_odd_reaction_pregame
      ? provider.static_odd_reaction_pregame.toString()
      : "0";
    oddSettings.pregame_odds_settings.randomFactor_pregame = provider.random_odd_reaction_pregame
      ? "Random Apply"
      : "Allways Apply";
    oddSettings.pregame_odds_settings.marginType_pregame = marginOptions.find(
      (mo) => mo.value == provider.margin_type_pregame
    );
    oddSettings.pregame_odds_settings.marginType_pregame = oddSettings.pregame_odds_settings
      .marginType_pregame
      ? oddSettings.pregame_odds_settings.marginType_pregame.label
      : "";
    oddSettings.pregame_odds_settings.marginPercent_pregame = provider.margin_percent_pregame;
    oddSettings.pregame_odds_settings.suspension_type_pregame = oddsSuspensionType.find(
      (ost) => ost.value == provider.suspension_type_pregame
    );

    return oddSettings;
  }

  async function initData() {
    //console.log("Wizard initData !", mode);
    if (template && template != "0") {
      let apiData = await getApiData(template);
      setApiData(apiData);
    } else {
      setActiveStep(-1);
    }
  }

  async function getApiData(template) {
    let getFiltersEndpoint = `/api/v1/templates/${template}/available-filters`;
    if (mode === "edit") getFiltersEndpoint = getFiltersEndpoint + "/" + filter;

    let response = await getData(getFiltersEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      let logoKeeper = {
        bet365: logoBet365,
        vbet: logoVbet,
        "1xbet": logo1xbet,
        stoiximan: logoStoiximan,
        mozzartbet: logoMozzartBet,
        betradar: logoBetradar,
        williamhill: logoWilliamhill,
        betanode: logoBetanode,
        draftkings: logoDraftkings,
        paddypower: logoPaddypower,
        bwin: logoBwin,
        sbobet: logoSbobet,
        sportingbet: logoSportingbet,
        "188bet": logo188bet,
        unibet: logoUnibet,
        feedthebet: logoFeedTheBet,
        fuse: logoFuse,
      };

      let myProviders = response.data.providers.map((el) => {
        el.location = el.location ? el.location : "-";
        el.weight = el.weight ? el.weight : "-";
        el.logo = logoKeeper[el.name];
        el.enabled = mode === "edit" ? !el.status : el.status;
        el.selected = mode === "edit" ? !el.status : false;
        el.odd_settings_inplay_info = el.inplay_odds_filter_id ? {} : null;
        el.odd_settings_pregame_info = el.pregame_odds_filter_id ? {} : null;
        el.oddSettings = extractOddSettings(el);
        el.marketSettings = el.markets_setting_id;
        return createProvidersData(
          el.id,
          el.name,
          el.location,
          el.weight,
          el.logo,
          el.enabled,
          el.oddSettings,
          el.marketSettings
        );
      });

      let mySports = response.data.sports.map((el) => {
        el.both = el.inplay ? (el.pregame ? true : false) : false;
        el.selected = mode === "edit" ? !el.status : false;

        el.default = el.hasOwnProperty("default_competitions") ? el.default_competitions : true;
        if (el.both) {
          el.inplay = false;
          el.pregame = false;
        }

        el.master =
          mode === "edit"
            ? el.master
            : response.data.sports.find(
                (item) =>
                  item.id == el.id &&
                  item.provider_id == 1000 &&
                  item.available &&
                  item.status == false
              )
            ? true
            : undefined;

        el.enabled = el.hasOwnProperty("available") ? el.available : true;
        el.disableType =
          response.data.providers.find((p) => p.id == el.provider_id) &&
          response.data.providers.find((p) => p.id == el.provider_id).inplay == false
            ? "inplay"
            : response.data.providers.find((p) => p.id == el.provider_id) &&
              response.data.providers.find((p) => p.id == el.provider_id).pregame == false
            ? "pregame"
            : null;

        // el.enabled =
        //   enabledProvider.length > 0
        //     ? enabledProvider[0].id == 1000
        //       ? el.hasOwnProperty("available")
        //         ? el.available
        //         : true
        //       : response.data.sports.find(
        //           (item) =>
        //             item.id == el.id &&
        //             item.provider_id == 1000 &&
        //             item.available &&
        //             item.status == false
        //         )
        //       ? false
        //       : el.hasOwnProperty("available")
        //       ? el.available
        //       : true
        //     : false;

        return createSportsData(
          el.id,
          el.name,
          el.inplay,
          el.pregame,
          el.both,
          el.default,
          el.selected,
          el.enabled,
          el.provider_id,
          el.master,
          el.odds_setting_id,
          el.odds_setting_inplay,
          el.odds_setting_pregame,
          el.markets_setting_id,
          el.is_esport,
          el.disableType,
          !!el.promos
        );
      });

      mySports.sort((a, b) => (a.name > b.name ? 1 : -1));
      setTemplateData(response.data.template);

      return { providers: myProviders, sports: mySports };
    } else {
      routeChange();
    }
  }

  function setApiData(apidata) {
    setInitProviders(apidata.providers);
    if (mode === "edit") {
      let initSports = apidata.sports.map((sport) => {
        let el = { ...sport };
        el.selected = false;
        return el;
      });
      setInitSports(initSports);
      let selectedProvider = apidata.providers.find((provider) => {
        if (provider.enabled) return provider;
      });

      let defaultStaticOddReactionJson = { value: 0, operator: "+" };

      let listItems = selectedProvider.oddSettings.inplay_odds_settings.odds_filter_inplay_id
        ? [
            {
              title: "Market Suspension",
              settings: [
                {
                  key: "Action",
                  value: selectedProvider.oddSettings.inplay_odds_settings.suspension_type_inplay
                    ? selectedProvider.oddSettings.inplay_odds_settings.suspension_type_inplay.label
                    : "No Action",
                },
                {
                  key: "Providers Priority for Suspension",
                  value: selectedProvider.oddSettings.inplay_odds_settings
                    .suspension_provider_inplay
                    ? selectedProvider.oddSettings.inplay_odds_settings.suspension_provider_inplay
                        .split(",")
                        .map((sp) => providers.find((p) => p.value == sp)["label"])
                    : "-",
                },
              ],
            },
            {
              title: "Odds Limit Reaction",
              settings: [
                {
                  key: "Action",
                  value: selectedProvider.oddSettings.inplay_odds_settings.oddsLimitReaction_inplay,
                },
                {
                  key: "min Price",
                  value: selectedProvider.oddSettings.inplay_odds_settings.lowerlimit_inplay,
                },
                {
                  key: "max Price",
                  value: selectedProvider.oddSettings.inplay_odds_settings.upperlimit_inplay,
                },
              ],
            },
            {
              title: "Mixing Type",
              settings: [
                {
                  key: "Action",
                  value: selectedProvider.oddSettings.inplay_odds_settings.mixingType_inplay,
                },
                {
                  key:
                    selectedProvider.oddSettings.inplay_odds_settings.mixingType_inplay ==
                    "No Mixing"
                      ? null
                      : "Mixing",
                  value: selectedProvider.oddSettings.inplay_odds_settings.mixingProviders_inplay
                    ? selectedProvider.oddSettings.inplay_odds_settings.mixingProviders_inplay
                    : "",
                },
                {
                  key: "Mixing Odd Limit",
                  value: selectedProvider.oddSettings.inplay_odds_settings.mixing_odd_limit_inplay
                    ? selectedProvider.oddSettings.inplay_odds_settings.mixing_odd_limit_inplay
                    : "-",
                },
              ],
            },
            {
              title: "Price Factor Behaviour",
              settings: [
                {
                  key: "Action",
                  value:
                    selectedProvider.oddSettings.inplay_odds_settings.static_odd_reaction &&
                    selectedProvider.oddSettings.inplay_odds_settings.static_odd_reaction.length ==
                      1 &&
                    JSON.stringify(
                      selectedProvider.oddSettings.inplay_odds_settings.static_odd_reaction[0].value
                    ) === JSON.stringify(defaultStaticOddReactionJson.value)
                      ? "Do Nothing"
                      : "Prices will be manipulated",
                },
              ],
            },
            {
              title: "Margin Behaviour",
              settings: [
                {
                  key: "Action",
                  value: selectedProvider.oddSettings.inplay_odds_settings.marginType_inplay,
                },
                {
                  key: "Margin",
                  value: selectedProvider.oddSettings.inplay_odds_settings.marginPercent_inplay
                    ? selectedProvider.oddSettings.inplay_odds_settings.marginPercent_inplay + "%"
                    : null,
                },
              ],
            },
          ]
        : null;

      selectedProvider.odd_settings_info_inplay = listItems;

      listItems = selectedProvider.oddSettings.pregame_odds_settings.odds_filter_pregame_id
        ? [
            {
              title: "Market Suspension",
              settings: [
                {
                  key: "Action",
                  value: selectedProvider.oddSettings.pregame_odds_settings.suspension_type_pregame
                    ? selectedProvider.oddSettings.pregame_odds_settings.suspension_type_pregame
                        .label
                    : "No Action",
                },
                {
                  key: "Providers Priority for Suspension",
                  value: selectedProvider.oddSettings.pregame_odds_settings
                    .suspension_provider_pregame
                    ? selectedProvider.oddSettings.pregame_odds_settings.suspension_provider_pregame
                        .split(",")
                        .map((sp) => providers.find((p) => p.value == sp)["label"])
                    : "-",
                },
              ],
            },
            {
              title: "Odds Limit Reaction",
              settings: [
                {
                  key: "Action",
                  value:
                    selectedProvider.oddSettings.pregame_odds_settings.oddsLimitReaction_pregame,
                },
                {
                  key: "min Price",
                  value: selectedProvider.oddSettings.pregame_odds_settings.lowerlimit_pregame,
                },
                {
                  key: "max Price",
                  value: selectedProvider.oddSettings.pregame_odds_settings.upperlimit_pregame,
                },
              ],
            },
            {
              title: "Mixing Type",
              settings: [
                {
                  key: "Action",
                  value: selectedProvider.oddSettings.pregame_odds_settings.mixingType_pregame,
                },
                {
                  key:
                    selectedProvider.oddSettings.pregame_odds_settings.mixingType_pregame ==
                    "No Mixing"
                      ? null
                      : "Mixing",
                  value: selectedProvider.oddSettings.pregame_odds_settings.mixingProviders_pregame
                    ? selectedProvider.oddSettings.pregame_odds_settings.mixingProviders_pregame
                    : "",
                },
                {
                  key: "Mixing Odd Limit",
                  value: selectedProvider.oddSettings.pregame_odds_settings.mixing_odd_limit_pregame
                    ? selectedProvider.oddSettings.pregame_odds_settings.mixing_odd_limit_pregame
                    : "-",
                },
              ],
            },
            {
              title: "Price Factor Behaviour",
              settings: [
                {
                  key: "Action",
                  value:
                    selectedProvider.oddSettings.pregame_odds_settings.static_odd_reaction &&
                    selectedProvider.oddSettings.pregame_odds_settings.static_odd_reaction.length ==
                      1 &&
                    JSON.stringify(
                      selectedProvider.oddSettings.pregame_odds_settings.static_odd_reaction[0]
                        .value
                    ) === JSON.stringify(defaultStaticOddReactionJson.value)
                      ? "Do Nothing"
                      : "Prices will be manipulated",
                },
              ],
            },
            {
              title: "Margin Behaviour",
              settings: [
                {
                  key: "Action",
                  value: selectedProvider.oddSettings.pregame_odds_settings.marginType_pregame,
                },
                {
                  key: "Margin",
                  value: selectedProvider.oddSettings.pregame_odds_settings.marginPercent_pregame
                    ? selectedProvider.oddSettings.pregame_odds_settings.marginPercent_pregame + "%"
                    : null,
                },
              ],
            },
          ]
        : null;

      selectedProvider.odd_settings_info_pregame = listItems;

      setProvider(selectedProvider);
      filterProvider = selectedProvider;

      setActiveStep(1);
    } else {
      if (template == "0" && mode == "create") {
        setActiveStep(-1);
      } else {
        setActiveStep(0);
      }
    }

    if (mode === "edit") {
      let selectedSports = apidata.sports.filter((sport) => sport.selected);
      setSports(selectedSports);
      setSportsArray(selectedSports);
    } else {
      setInitSports(apidata.sports);
    }
  }

  function createProvidersData(
    id: number,
    name: string,
    location: string,
    weight: string,
    logo,
    enabled: boolean,
    oddSettings: object,
    marketSettings: number
  ) {
    return { id, name, location, weight, logo, enabled, oddSettings, marketSettings };
  }

  function createSportsData(
    id: number,
    name: string,
    inplay: boolean,
    pregame: boolean,
    both: boolean,
    defaultvalue: boolean,
    selected: boolean,
    enabled: boolean,
    providerId: number,
    masterFeed: boolean,
    odds_setting_id: number,
    odds_setting_inplay: number,
    odds_setting_pregame: number,
    markets_setting_id: number,
    is_esport: boolean,
    disableType: string,
    promos: boolean
  ) {
    return {
      id,
      name,
      inplay,
      pregame,
      both,
      defaultvalue,
      selected,
      enabled,
      providerId,
      masterFeed,
      odds_setting_id,
      odds_setting_inplay,
      odds_setting_pregame,
      markets_setting_id,
      is_esport,
      disableType,
      promos,
    };
  }

  const handleNext = async () => {
    setPreviousStep(activeStep);
    setActiveStep(activeStep + 1);
    //console.log("handleNext: ", activeStep + 1);
    setShowDialog(true);
    switch (activeStep + 1) {
      case 0:
        //console.log("dataCheck:Provider:", JSON.stringify(provider));
        if (provider) setStepDisabled(false);
        return;
        break;
      case 1:
        //console.log("dataCheck:Sports:", JSON.stringify(sports), JSON.stringify(sportsArray));
        if (sportsArray && sportsArray.length > 0) {
          setStepDisabled(false);
        } else {
          setStepDisabled(true);
        }
        return;
        break;
      case 2:
        setStepDisabled(false);
        return;
        break;
      case 3:
        setStepDisabled(false);
        return;
        break;
      case 4:
        // console.log("dataCheck:Odds:", JSON.stringify(oddsFilters));
        // setStepDisabled(false);
        // return;
        // break;
        setStepDisabled(false);
        return;
        break;
      case 5:
      // setStepDisabled(false);
      // return;
      // break;
      default:
        setStepDisabled(true);
        break;
    }
    setStepDisabled(true);
  };

  const handleBack = () => {
    setPreviousStep(activeStep);
    setActiveStep(activeStep - 1);
    setStepDisabled(true);
    //console.log("handleBack: ", activeStep - 1);
    switch (activeStep - 1) {
      case 0:
        //console.log("dataCheck:Provider:", JSON.stringify(provider));
        if (provider) setStepDisabled(false);
        break;
      case 1:
        //console.log("dataCheck:Sports:", JSON.stringify(sports), JSON.stringify(sportsArray));
        if (sportsArray && sportsArray.length > 0) setStepDisabled(false);
        break;
      case 2:
        setStepDisabled(false);
        break;
      case 3:
        setStepDisabled(false);
        //console.log("handleBack");
        filterCompetitions = competitionsArray.map((c) => {
          c.selected = false;
          return c;
        });

        setCompetitionsArray(filterCompetitions);
        break;
      case 4:
        // console.log("dataCheck:Odds:", JSON.stringify(oddsFilters));
        // setStepDisabled(false);
        // break;
        setStepDisabled(false);
        break;
      case 5:
      // setStepDisabled(false);
      // break;
      default:
        break;
    }
  };

  const saveWizardData = async () => {
    //console.log("Creation completed, please Update!");
    let objSent = { templateId: templateId, filters: [] };

    let filterEntry = {
      provider: { id: provider.id, inplay: false, pregame: false },
      sports: [],
      competitions: [],
      promoCompetitions: [],
      oddsConfig: [],
      marketsConfig: [],
      odds: [],
    };

    for (const key in sports) {
      if (Object.hasOwnProperty.call(sports, key)) {
        const sport = sports[key];
        let sportTopush = { id: 0, inplay: false, pregame: false, promos: false };
        let oddSportConfigTopush = {
          sport_id: 0,
          competition_id: null,
          market_id: null,
          odds_setting_inplay: 0,
          odds_setting_pregame: 0,
        };
        let sportMarketsConfigTopush = {
          sport_id: 0,
          competition_id: null,
          markets_setting: 0,
        };
        if (sport.inplay) filterEntry.provider.inplay = true;
        if (sport.pregame) filterEntry.provider.pregame = true;
        sportTopush.id = sport.id;
        sportTopush.inplay = sport.inplay;
        sportTopush.pregame = sport.pregame;
        sportTopush.promos = !!sport.promos;
        sportTopush.default = sport.defaultvalue;

        if (sport.both) {
          filterEntry.provider.inplay = true;
          filterEntry.provider.pregame = true;
          sportTopush.inplay = true;
          sportTopush.pregame = true;
        }
        if (sport.odds_setting_inplay || sport.odds_setting_pregame) {
          oddSportConfigTopush.sport_id = sport.id;
          oddSportConfigTopush.odds_setting_inplay = sport.odds_setting_inplay;
          oddSportConfigTopush.odds_setting_pregame = sport.odds_setting_pregame;
          filterEntry.oddsConfig.push(oddSportConfigTopush);
        }
        if (sport.markets_setting_id) {
          sportMarketsConfigTopush.sport_id = sport.id;
          sportMarketsConfigTopush.markets_setting = sport.markets_setting_id;
          filterEntry.marketsConfig.push(sportMarketsConfigTopush);
        }
        filterEntry.sports.push(sportTopush);
      }
    }

    for (const key in competitionsArray) {
      if (Object.hasOwnProperty.call(competitionsArray, key)) {
        const competition = competitionsArray[key];
        const competitionSport = sports.find((sport) => sport.name == competition.sport);

        if (competition.promos && competition.uni_id && competitionSport) {
          const parsedUniId = parseInt(competition.uni_id);
          const uniId = Number.isNaN(parsedUniId) ? competition.uni_id : parsedUniId;
          const parsedSportId = parseInt(competitionSport.id);
          const sportId = Number.isNaN(parsedSportId)
            ? competitionSport.id
            : parsedSportId;
          const promoCompetition = {
            uni_id: uniId,
            sport_id: sportId,
          };

          if (
            !filterEntry.promoCompetitions.find(
              (item) => item.uni_id === uniId && item.sport_id === sportId
            )
          ) {
            filterEntry.promoCompetitions.push(promoCompetition);
          }
        }

        let oddCompConfigTopush = {
          sport_id: 0,
          competition_id: 0,
          market_id: null,
          odds_setting_inplay: 0,
          odds_setting_pregame: 0,
        };

        let compMarketsConfigTopush = {
          sport_id: 0,
          competition_id: 0,
          markets_setting: 0,
        };

        if (competition.odds_setting_inplay || competition.odds_setting_pregame) {
          oddCompConfigTopush.sport_id = competitionSport.id;
          oddCompConfigTopush.competition_id = parseInt(competition.id);
          oddCompConfigTopush.odds_setting_inplay = competition.odds_setting_inplay;
          oddCompConfigTopush.odds_setting_pregame = competition.odds_setting_pregame;
          filterEntry.oddsConfig.push(oddCompConfigTopush);
        }

        if (competition.markets_setting_id) {
          compMarketsConfigTopush.sport_id = competitionSport.id;
          compMarketsConfigTopush.competition_id = parseInt(competition.id);
          compMarketsConfigTopush.markets_setting = competition.markets_setting_id;
          filterEntry.marketsConfig.push(compMarketsConfigTopush);
        }

        if (competition.inherit) continue;
        let competitionTopush = {
          id: 0,
          sportId: 0,
          inplay: false,
          pregame: false,
          promos: false,
        };
        competitionTopush.id = competition.id;
        competitionTopush.sportId = competitionSport.id;
        competitionTopush.inplay = competition.inplay === null ? false : competition.inplay;
        competitionTopush.pregame = competition.pregame === null ? false : competition.pregame;
        competitionTopush.promos = competition.promos === null ? false : !!competition.promos;

        if (
          !filterEntry.competitions.find(
            (competition) =>
              competition.id === competitionTopush.id &&
              competition.sportId === competitionTopush.sportId
          )
        )
          filterEntry.competitions.push(competitionTopush);
      }
    }

    let oddsFiltersToSend = JSON.parse(JSON.stringify(oddsFilters));
    if (Object.keys(oddsFiltersToSend).length !== 0) {
      oddsFiltersToSend.barrier_reaction_type = oddsFilters.barrier_reaction_type.value;
      oddsFiltersToSend.mixing_odd_type = oddsFilters.mixing_odd_type.value;
      oddsFiltersToSend.mixing_provider_id = oddsFilters.mixing_provider_id.value;
      filterEntry.odds.push(oddsFiltersToSend);
    } else {
      delete filterEntry.odds;
    }

    objSent.filters.push(filterEntry);

    function returnId() {
      return templateId;
    }

    function returnData() {
      return objSent;
    }

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let setTemplatesFiltersEndpoint = `/api/v1/templates/${returnId()}/filters`;
        let response = await postData(setTemplatesFiltersEndpoint, returnData(), {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      setShowDialog(false);
      if (result.isConfirmed) {
        Swal.fire({
          title: "Request Sent!",
          html: `Live Data sent to the platform. </br>Continue to Filters List Page!`,
          icon: "success",
          confirmButtonText: "Ok",
        }).then((result) => {
          if (result.isConfirmed) {
            //showAlert();
            routeChange();
          }
        });
      }
      if (result.isDenied) {
        Swal.fire({
          title: "Error!",
          html: `Live Data didn't send to the platform. Please try again later! </br>You are about to redirect automatically to Filters List Page!`,
          icon: "error",
          confirmButtonText: "Ok",
        }).then((result) => {
          if (result.isConfirmed) {
            //showAlert();
            routeChange();
          }
        });
      }
    });
  };

  let navigate = useNavigate();
  const routeChange = () => {
    // let path = `/feed-settings/templates/${templateId}/filters-list`;
    // let path = `/feed-settings/filters/filters-list/${templateId}`;
    let path = `/feed-settings/filters`;
    let options = templateId ? { state: { templateId: templateId } } : {};
    navigate(path, options);
  };

  const handleStepperButtonStatus = (disable) => {
    //console.log("handleStepperButtonStatus:", disable);
    setStepDisabled(disable);
  };

  const handleStepData = (data) => {
    switch (activeStep) {
      case 0:
        if (provider && data.id !== provider.id) {
          if (mode === "edit") {
            // setSports(initSports);
            // setSportsArray(initSports);
          } else {
            setSports([]);
            setSportsArray([]);
          }
        }

        setProvider(data);
        break;
      case 1:
        checkInitCompetitions(filterSports, data);
        filterSports = data;
        setSports(data);
        setSportsArray(data);
        //console.log("Wizard:Get Selected sports:", JSON.stringify(data));
        break;
      case 2:
        if (data && data.length > 0) {
          filterSports = data;
          setSports(data);
          setSportsArray(data);
        }
        filterCompetitions = [];
        setCompetitionsArray([]);
        break;
      case 3:
        //console.log("handleStepData");
        filterCompetitions = data;
        setCompetitionsArray(data);
        //console.log("Wizard:Get Selected Competitions:", JSON.stringify(data));
        break;
      case 4:
        // setOddsFilters(data);
        // console.log("Wizard:Get Selected Odds Filters:", JSON.stringify(data));
        // break;
        let navigate = useNavigate();
        break;
      case 5:
        // let navigate = useNavigate();
        // const routeChange = () => {
        //   let path = `/feed-settings/templates/${templateCreatedId}/filters-list`;
        //   navigate(path);
        // };
        break;
      default:
        break;
    }
  };

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return (
          <Provider
            mode={mode}
            initData={initProviders}
            filterData={provider}
            handleStepperButtonStatus={handleStepperButtonStatus}
            handleStepData={handleStepData}
          />
        );
      case 1:
        return (
          <Sports
            provider={provider ? provider.id : 0}
            initData={initSports}
            filterData={sports}
            handleStepperButtonStatus={handleStepperButtonStatus}
            handleStepData={handleStepData}
          />
        );
      case 2:
        return (
          <SportsEventType
            filterData={sportsArray}
            handleStepperButtonStatus={handleStepperButtonStatus}
            handleStepData={handleStepData}
            showSportPromosSwitch={showSportPromosSwitch}
          />
        );
      case 3:
        return (
          <Competitions
            provider={provider}
            template={templateId}
            initData={initCompetitions}
            filterSportData={sportsArray}
            filterCompetitionData={competitionsArray}
            handleStepperButtonStatus={handleStepperButtonStatus}
            handleStepData={handleStepData}
            showSportPromosSwitch={showSportPromosSwitch}
            showCompetitionPromosSwitch={showCompetitionPromosSwitch}
          />
        );
      case 4:
        // return (
        //   <Odds
        //     provider={provider}
        //     initData={{ providers: initProviders, template: templateId }}
        //     filterData={oddsFilters}
        //     handleStepperButtonStatus={handleStepperButtonStatus}
        //     handleStepData={handleStepData}
        //   />
        // );
        return (
          <SaveTemplate
            provider={provider}
            template={templateId}
            filterSportData={sportsArray}
            filterComptetitionData={competitionsArray}
            handleStepperButtonStatus={handleStepperButtonStatus}
            handleStepData={handleStepData}
          />
        );
      case 5:
      // return (
      //   <SaveTemplate
      //     provider={provider}
      //     filterSportData={sportsArray}
      //     filterComptetitionData={competitionsArray}
      //     handleStepperButtonStatus={handleStepperButtonStatus}
      //     handleStepData={handleStepData}
      //   />
      // );
      default:
        return null;
    }
  }

  async function setParentSelectedType(item) {
    //console.log(item);
    template = item.value.toString();
    setTemplateId(template);
    let apiData = await getApiData(template);
    if (template != "0" && apiData) {
      setApiData(apiData);
      setTemplateData({ name: item.label, id: item.id });
    } else {
      setActiveStep(-1);
    }
  }

  function getAvailableTemplates(stepIndex) {
    switch (stepIndex) {
      case -1:
        return (
          <AvailableTemplates setParentSelectedType={setParentSelectedType}></AvailableTemplates>
        );
      default:
        return null;
    }
  }

  function enableCancelCreationModal() {
    setCreateModal(true);
  }

  const handleCloseModal = (update) => {
    if (update) {
      //console.log("Creation completed, please Update!");
      setShowDialog(false);
    } else {
      setShowDialog(true);
      //console.log("Creation canceled, do nothing!");
    }
    setCreateModal(false);
  };

  function checkInitCompetitions(prevData, curData) {
    let toInit = false;

    toInit = true;

    setCompetitionInit(toInit);
  }

  useEffect(() => {
    //console.log("previousStep:", previousStep, "activeStep:", activeStep);
    if (previousStep == 2 && previousStep !== activeStep) {
      setSports(sportsArray);
    }
    if (previousStep == 3 && previousStep !== activeStep && previousStep < activeStep) {
      //console.log("Last Step Clicked!!!");
      setCompetitions(competitionsArray);
    }
  }, [previousStep, activeStep]);

  useEffect(() => {
    //initData();
  }, [href]);

  useEffect(() => {
    if (platform) setPlatformData(platform);
  }, [platform]);

  useEffect(() => {
    fetchPromoPermissions();
  }, [permissions, platform]);

  useEffect(() => {
    if (location) {
      //console.log("wizard selected: ", location.state);
      if (location.pathname.indexOf("edit") > -1) {
        template = location.state.stateTemplate + "";
        filter = location.state.stateFilter;
        setTemplateId(template);
      } else {
        template = "0";
        filter = null;
        setTemplateId(template);
      }

      initData();
    }
  }, [location]);

  return (
    <DashboardLayout>
      <DialogBox
        // @ts-ignore
        showDialog={showPrompt}
        confirmNavigation={confirmNavigation}
        cancelNavigation={cancelNavigation}
      />
      <DashboardNavbar />
      <CheckFilterCreation
        reference={templateId}
        mode={mode}
        showModal={showCreateModal}
        signalCloseModal={handleCloseModal}
      ></CheckFilterCreation>

      <SoftBox pt={3} pb={8}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={10}>
            <Card
              sx={{
                overflow: "visible",
                background: "#ffffffcf !important",
                padding: "20px !important",
              }}
            >
              <SoftBox
                textAlign="center"
                style={{
                  display: templateData ? "flex" : "none",
                  position: "absolute",
                  top: "16px",
                  width: "fit-content",
                  left: "24px",
                  opacity: "0.5",
                }}
              >
                <SoftTypography variant="h6" fontWeight="regular" color="dark" display="flex">
                  {platformData ? platformData.label : "-"}{" "}
                  <Icon
                    style={{
                      fontSize: "1em",
                      marginTop: "0.6em",
                      color: "#acacac",
                      lineHeight: "0.6em",
                    }}
                  >
                    chevron_right_icon{" "}
                  </Icon>{" "}
                  {templateData ? templateData.name : "-"}
                </SoftTypography>
              </SoftBox>

              {/* <SoftBox
                textAlign="center"
                sx={{
                  display: provider ? "" : "none",
                  position: "absolute",
                  top: "46px",
                  width: "130px",
                  transform: "scale(50%)",
                  transformOrigin: "0 0",
                  opacity: "0.5",
                }}
              >
                <SoftButton
                  className={provider ? "provider-button-" + provider.name : ""}
                  color="light"
                  variant="contained"
                  title="Selected Provider for this Filter"
                >
                  <SoftAvatar
                    src={provider ? provider.logo : ""}
                    alt="Selected Provider for this Filter"
                    size="xl"
                    variant="rounded"
                  />
                </SoftButton>
              </SoftBox> */}
              <SoftBox
                mb={1}
                textAlign="right"
                sx={{
                  position: "relative",
                  top: "0px",
                }}
              >
                <Tooltip
                  title={mode == "create" ? "Cancel Filter Creation" : "Cancel Filter Update"}
                  placement="left"
                >
                  <SoftButton id="close-wizard-button" onClick={enableCancelCreationModal}>
                    <Icon id="close-wizard">close_icon</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>
              <SoftBox mt={6} mb={1} textAlign="center">
                <SoftBox mb={1}>
                  <SoftTypography
                    variant="h3"
                    fontWeight="bold"
                    style={{
                      display: "flex",
                      width: "fit-content",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                  >
                    {mode === "create" ? "Create a new Filter " : "Edit Filter "}
                    <SoftTypography
                      sx={{
                        fontFamily:
                          "-apple-system,BlinkMacSystemFont,Helvetica Neue,Segoe UI,Tahoma,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Open Sans,sans-serif",
                        fontSize: "2rem",
                        fontWeight: "800",
                        lineHeight: "2.2rem",
                        fontStyle: "italic",
                        color: provider ? customColors[provider.name] : "inherit",
                        marginLeft: "10px",
                      }}
                    >
                      {provider ? provider.name : ""}
                    </SoftTypography>
                  </SoftTypography>
                </SoftBox>
                <SoftTypography variant="h5" fontWeight="regular" color="secondary">
                  This steps will help you to customize the feed you get
                </SoftTypography>
                <SoftBox className="filter-odd-settings-info">
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="regular" color="secondary">
                      Markets Settings
                    </SoftTypography>
                    <HtmlTooltip
                      placement="right"
                      title={
                        <React.Fragment>
                          <div style={{}}>
                            {provider && provider.marketSettings
                              ? provider.marketSettings
                              : "no Markets Settings Assigned"}
                          </div>
                        </React.Fragment>
                      }
                    >
                      <Button
                        sx={{
                          maxWidth: "15px",
                          width: "15px",
                          minWidth: "10px",
                          marginRight: "5px",
                          color: provider && provider.marketSettings ? "info" : "red",
                        }}
                      >
                        <Icon>
                          {provider && provider.marketSettings
                            ? "info_outlined_icon"
                            : "error_outline_outlined_icon "}
                        </Icon>
                      </Button>
                    </HtmlTooltip>
                  </SoftBox>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="regular" color="secondary">
                      Odds Settings inPlay
                    </SoftTypography>
                    <HtmlTooltip
                      placement="right"
                      title={
                        <React.Fragment>
                          <div style={{}}>
                            {provider && provider.odd_settings_info_inplay
                              ? provider.odd_settings_info_inplay.map((item, index) => {
                                  return (
                                    <div key={index} style={{}}>
                                      <div style={{ fontWeight: "bold" }}>{item.title}</div>
                                      <ul style={{ marginLeft: "5px", paddingLeft: "10px" }}>
                                        {item.settings.map((s, i) => {
                                          let print = s.value
                                            ? s.key
                                              ? s.key + ": " + s.value
                                              : s.value
                                            : "";
                                          return print == "" ? false : <li key={i}>{print}</li>;
                                        })}
                                      </ul>
                                    </div>
                                  );
                                })
                              : "no Odds Settings Assigned"}
                          </div>
                        </React.Fragment>
                      }
                    >
                      <Button
                        sx={{
                          maxWidth: "15px",
                          width: "15px",
                          minWidth: "10px",
                          marginRight: "5px",
                          color: provider && provider.odd_settings_info_inplay ? "info" : "red",
                        }}
                      >
                        <Icon>
                          {provider && provider.odd_settings_info_inplay
                            ? "info_outlined_icon"
                            : "error_outline_outlined_icon "}
                        </Icon>
                      </Button>
                    </HtmlTooltip>
                  </SoftBox>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="regular" color="secondary">
                      Odds Settings preGame
                    </SoftTypography>
                    <HtmlTooltip
                      placement="right"
                      title={
                        <React.Fragment>
                          <div style={{}}>
                            {provider && provider.odd_settings_info_pregame
                              ? provider.odd_settings_info_pregame.map((item, index) => {
                                  return (
                                    <div key={index} style={{}}>
                                      <div style={{ fontWeight: "bold" }}>{item.title}</div>
                                      <ul style={{ marginLeft: "5px", paddingLeft: "10px" }}>
                                        {item.settings.map((s, i) => {
                                          let print = s.value
                                            ? s.key
                                              ? s.key + ": " + s.value
                                              : s.value
                                            : "";
                                          return print == "" ? false : <li key={i}>{print}</li>;
                                        })}
                                      </ul>
                                    </div>
                                  );
                                })
                              : "no Odds Settings Assigned"}
                          </div>
                        </React.Fragment>
                      }
                    >
                      <Button
                        sx={{
                          maxWidth: "15px",
                          width: "15px",
                          minWidth: "10px",
                          marginRight: "5px",
                          color: provider && provider.odd_settings_info_pregame ? "info" : "red",
                        }}
                      >
                        <Icon>
                          {provider && provider.odd_settings_info_pregame
                            ? "info_outlined_icon"
                            : "error_outline_outlined_icon "}
                        </Icon>
                      </Button>
                    </HtmlTooltip>
                  </SoftBox>
                </SoftBox>
              </SoftBox>

              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Card style={{ overflow: "visible" }}>
                <SoftBox p={2}>
                  <SoftBox>
                    {getAvailableTemplates(activeStep)}
                    {getStepContent(activeStep)}
                    <SoftBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      {activeStep === 0 || activeStep == -1 ? (
                        <SoftBox />
                      ) : (
                        <SoftButton
                          disabled={activeStep == -1 || (mode === "edit" && activeStep == 1)}
                          variant="gradient"
                          color="light"
                          onClick={handleBack}
                        >
                          back
                        </SoftButton>
                      )}
                      <SoftButton
                        disabled={stepDisabled}
                        variant="gradient"
                        color={isLastStep ? "warning" : "dark"}
                        onClick={!isLastStep ? handleNext : saveWizardData}
                      >
                        {isLastStep ? "save" : "next"}
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

// Typechecking props for the DataTable
Wizard.propTypes = {
  template: PropTypes.string,
  filter: PropTypes.string,
  mode: PropTypes.string,
  filterProvider: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
  filterSports: PropTypes.array,
  filterCompetitions: PropTypes.array,
};

export default Wizard;
